import React from "react";
import {
  Info,
  AlertTriangle,
  CheckCircle,
  AlertCircle,
  LucideIcon,
} from "lucide-react";

// Define the available variants
type InfoboxVariant = "info" | "success" | "warning" | "error";

interface InfoboxProps {
  children: React.ReactNode;
  title?: string;
  variant?: InfoboxVariant;
  className?: string;
}

// Map variants to specific styles and icons
const variantStyles: Record<
  InfoboxVariant,
  {
    container: string;
    iconColor: string;
    Icon: LucideIcon;
  }
> = {
  info: {
    container: "bg-blue-50 border-blue-200 text-blue-900",
    iconColor: "text-blue-600",
    Icon: Info,
  },
  success: {
    container: "bg-emerald-50 border-emerald-200 text-emerald-900",
    iconColor: "text-emerald-600",
    Icon: CheckCircle,
  },
  warning: {
    container: "bg-amber-50 border-amber-200 text-amber-900",
    iconColor: "text-amber-600",
    Icon: AlertTriangle,
  },
  error: {
    container: "bg-red-50 border-red-200 text-red-900",
    iconColor: "text-red-600",
    Icon: AlertCircle,
  },
};

const Infobox: React.FC<InfoboxProps> = ({
  children,
  title,
  variant = "info",
  className = "",
}) => {
  const { container, iconColor, Icon } = variantStyles[variant];

  return (
    <div
      className={`relative flex w-full gap-3 rounded-lg border border-l-4 p-4 shadow-sm ${container} ${className}`}
      role="alert"
    >
      {/* Icon Section */}
      <div className="flex-shrink-0">
        <Icon className={`h-5 w-5 ${iconColor}`} aria-hidden="true" />
      </div>

      {/* Content Section */}
      <div className="flex flex-col gap-1">
        {title && (
          <h3 className="text-sm leading-none font-semibold tracking-tight">
            {title}
          </h3>
        )}
        <div className="text-sm opacity-90">{children}</div>
      </div>
    </div>
  );
};

export default Infobox;
